import axios from 'axios';
import type { Dispatch, SetStateAction } from 'react';
import { NewPlanStages } from 'components/newPlan/constants';
import { mapFormConfigs } from 'components/login/helpers';
import { callValidateGSTDetails } from 'utils/api/services';
import { ValidateGSTDetailsResponse } from 'utils/api/types';
import {
    autoLobPartners,
    autoPartnershipPartners,
    FORM_TYPES,
} from 'utils/constants';
import { getAxiosConfig, getDomainOrigin } from 'utils/serverConfig';

interface BootstrapUserPermissionsArgs {
    router: any;
    partnerId: string;
    groupName: string;
    setPermissions: (permissions: any) => void;
    setUserDetails: (userDetails: any) => void;
    setMetaData: (metaData: any) => void;
    setPartnerId: (partnerId: string) => void;
    setShowErrorScreen: (value: boolean) => void;
    setPermissionsMap: (permissionsMap: Record<string, boolean>) => void;
    setDealerType?: (dealerType: string) => void;
    setLoggedUserRole: (role: string) => void;
    setLegalDetailsUploadedStatus: (value: boolean) => void;
    setActiveStage: Dispatch<SetStateAction<NewPlanStages>>;
    setGstDetailsValid: (value: boolean) => void;
    setGstDetailsSubmitAction: (value: boolean) => void;
    setFormConfigsMap?: (formConfigsMap: any) => void;
    setSnackbar: (snackbar: { type: string; message: string }) => void;
    setErrorMessage: (value: string) => void;
    setIsLoading: (value: boolean) => void;
}

export async function bootstrapUserPermissions({
    router,
    partnerId,
    groupName,
    setPermissions,
    setUserDetails,
    setMetaData,
    setPartnerId,
    setShowErrorScreen,
    setPermissionsMap,
    setDealerType,
    setLoggedUserRole,
    setLegalDetailsUploadedStatus,
    setActiveStage,
    setGstDetailsValid,
    setGstDetailsSubmitAction,
    setFormConfigsMap,
    setSnackbar,
    setErrorMessage,
    setIsLoading,
}: BootstrapUserPermissionsArgs) {
    try {
        const { status, data } = await axios({
            method: 'GET',
            url: `${getDomainOrigin()}/api/mpos/partner/${partnerId}/user-permissions?group_name=${groupName}`,
            ...getAxiosConfig(),
        });
        if (status === 200 && data?.permissions) {
            const { permissions, user_details, meta_data } = data;
            setPermissions(permissions);
            setUserDetails(user_details);
            setMetaData(meta_data);
            setPartnerId(user_details?.partner_id);
            setShowErrorScreen(false);
            const permissionsMap: Record<string, boolean> = {};
            permissions?.permission_list?.forEach((permission: string) => {
                permissionsMap[permission] = true;
            });
            setPermissionsMap(permissionsMap);
            if (setDealerType) {
                setDealerType(permissions?.role || '');
            }

            let lobPrefix;
            if (permissions?.lob) {
                lobPrefix = permissions.lob;
            } else {
                lobPrefix =
                    autoLobPartners.includes(user_details.partner_id) ||
                    permissions.permission_list.includes(
                        'access_only_auto_commercial_flow'
                    )
                        ? 'auto-lob'
                        : autoPartnershipPartners.includes(
                              user_details.partner_id
                          )
                        ? 'auto-partnership'
                        : 'embedded';
            }

            if (router?.query?.slug?.includes('thankyou')) {
                setIsLoading(false);
                return;
            }
            const user_role = permissions.group;
            if (!user_details?.partner_id || !user_role) {
                setIsLoading(false);
                router.push('/partners');
                return;
            }

            try {
                const { status: statusCode, data: formConfigs } = await axios({
                    method: 'GET',
                    url: `${getDomainOrigin()}/api/mpos/genie/onboarding/${
                        user_details.partner_id
                    }/details/status?user_group=${user_role}`,
                    ...getAxiosConfig(),
                });

                if (statusCode === 200 && Array.isArray(formConfigs)) {
                    const newFormConfigsMap = mapFormConfigs(formConfigs);
                    if (setFormConfigsMap) {
                        setFormConfigsMap(newFormConfigsMap);
                    }

                    const dealerDetailsForm =
                        newFormConfigsMap[FORM_TYPES.DEALER_DETAILS];
                    if (dealerDetailsForm) {
                        const {
                            should_capture_details,
                            details_update_required,
                        } = dealerDetailsForm;
                        if (should_capture_details || details_update_required) {
                            setIsLoading(false);
                            router.push(
                                `/${lobPrefix}/${user_details.partner_id}/dealer-details`
                            );
                            return;
                        }
                    }
                }
            } catch (e: any) {
                if (e?.response?.status === 404) {
                    setErrorMessage(e?.response?.data?.message);
                }
                setShowErrorScreen(true);
                setIsLoading(false);
                return;
            }

            try {
                const { data: financialDetailsData, status: financialStatus } =
                    await axios({
                        method: 'GET',
                        url: `${getDomainOrigin()}/api/mpos/${
                            user_details.partner_id
                        }/financial-details/exists?group_name=${user_role}`,
                        ...getAxiosConfig(),
                    });
                if (financialStatus === 200 && financialDetailsData) {
                    const { details_required, details_present, can_submit } =
                        financialDetailsData;
                    setLoggedUserRole(user_role);
                    if (details_required && !details_present) {
                        setLegalDetailsUploadedStatus(true);
                        if (can_submit) {
                            setActiveStage(
                                NewPlanStages.LEGAL_DOCUMENTS_UPLOAD
                            );
                            setIsLoading(false);
                            router.push(
                                `/${lobPrefix}/${user_details.partner_id}/legal-documents`
                            );
                            return;
                        }
                        if (user_role === 'sub_dealer') {
                            setIsLoading(false);
                            router.push(
                                `/${lobPrefix}/${user_details.partner_id}/dashboard`
                            );
                            return;
                        }
                    }

                    if (
                        permissions.permission_list.includes(
                            'access_only_auto_commercial_flow'
                        )
                    ) {
                        setIsLoading(false);
                        router.push(
                            `/${lobPrefix}/${user_details.partner_id}/form`
                        );
                        return;
                    }

                    const gstValidationResponse: ValidateGSTDetailsResponse =
                        await callValidateGSTDetails(
                            user_details.partner_id,
                            user_role,
                            true
                        );

                    if (!gstValidationResponse.is_error) {
                        if (gstValidationResponse.data) {
                            const { required, valid, can_submit } =
                                gstValidationResponse.data;
                            if (required && !valid) {
                                setGstDetailsValid(false);
                            } else {
                                setGstDetailsValid(true);
                            }
                            setGstDetailsSubmitAction(can_submit);
                        }
                    } else {
                        setSnackbar({
                            type: 'failure',
                            message: 'Failed to validate GST Details',
                        });
                    }
                    setIsLoading(false);
                    await router.replace(
                        `/${lobPrefix}/${user_details.partner_id}/dashboard`
                    );
                    return;
                }
            } catch (e: any) {
                if (e?.response?.status === 401) {
                    setIsLoading(false);
                    router.push('/login');
                    return;
                }
                const errorMessage = e?.response?.data?.message;
                setErrorMessage(errorMessage);
                setShowErrorScreen(true);
                setIsLoading(false);
            }
        }
    } catch (e: any) {
        if (e?.response?.status === 401) {
            setSnackbar({
                type: 'failure',
                message: 'Token expired. Redirecting to login page',
            });
            setIsLoading(false);
            router.push('/login');
            return;
        }
        const errorMessage = e?.response?.data?.message;
        setErrorMessage(errorMessage);
        setShowErrorScreen(true);
        setIsLoading(false);
    }
}
